import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Socket, io } from "socket.io-client";

import { SOCKET_URL, SPORT_TEST_MODE } from "@/configs";
import { useToken } from "@/hooks";
import { EventsByStatusResponse, SportEvent, SportRegionGroup } from "@/types";

import { setSportSocketConnected } from "../utils/socketStatus";

const NAMESPACE = "/sport";

/** A sport's own in-play page lists the same events as its group in the in-play list. */
const fillSportLiveLists = (queryClient: QueryClient, live: EventsByStatusResponse) => {
  const bySlug = new Map(live.sports.map((group) => [group.sport.slug, group]));
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ["sport-events"] })) {
    const [, sportSlug, eventStatus] = query.queryKey as [string, string, string];
    if (eventStatus !== "live") continue;
    const group = bySlug.get(sportSlug);
    queryClient.setQueryData<SportRegionGroup>(query.queryKey, {
      eventCount: group?.eventCount ?? 0,
      leagues: group?.leagues ?? [],
    });
  }
};

const scoreKey = (event?: Pick<SportEvent, "status" | "scores">) =>
  event ? `${event.status}|${event.scores?.home}|${event.scores?.away}` : "";

/**
 * Takes the server's pushes for the event on screen and the in-play list straight into
 * the query cache: Betaxis tells the server as soon as a price, score or final result
 * changes, so the page follows within a second or two. One connection for the whole
 * sportsbook (the layout holds it); the polls stay as the fallback when it is down.
 */
export const useSportSocket = ({
  eventIds = [],
  live = false,
}: {
  eventIds?: number[];
  live?: boolean;
}) => {
  const queryClient = useQueryClient();
  const token = useToken();
  const socketRef = useRef<Socket | null>(null);
  const watched = eventIds.join(",");
  // Read on (re)connect, so a reconnect re-sends what is on screen now.
  const watchRef = useRef({ eventIds, live });
  watchRef.current = { eventIds: watched ? watched.split(",").map(Number) : [], live };

  useEffect(() => {
    if (!SOCKET_URL) return;
    const socket = io(`${SOCKET_URL}${NAMESPACE}`, { auth: token ? { token } : {} });
    socketRef.current = socket;

    socket.on("connect", () => {
      setSportSocketConnected(true);
      socket.emit("watch", watchRef.current);
    });
    socket.on("disconnect", () => setSportSocketConnected(false));
    socket.on("sportEvent", (payload: { eventId: number; event: SportEvent | null }) => {
      if (!payload?.event) return;
      const key = ["sport-event", String(payload.eventId)];
      // The push carries only the markets players see; the test build refetches its full list.
      if (SPORT_TEST_MODE) {
        void queryClient.invalidateQueries({ queryKey: key });
        return;
      }
      const previous = queryClient.getQueryData<SportEvent>(key);
      queryClient.setQueryData(key, payload.event);
      // A goal or the final whistle: the tracker's incidents changed too.
      if (previous && scoreKey(previous) !== scoreKey(payload.event)) {
        void queryClient.invalidateQueries({ queryKey: ["sport-event-live", payload.eventId] });
      }
    });
    socket.on("sportLive", (payload: EventsByStatusResponse) => {
      if (!payload?.sports) return;
      queryClient.setQueryData(["events-by-status", "live"], payload);
      fillSportLiveLists(queryClient, payload);
    });

    return () => {
      socketRef.current = null;
      socket.disconnect();
      setSportSocketConnected(false);
    };
  }, [token, queryClient]);

  useEffect(() => {
    const socket = socketRef.current;
    if (socket?.connected) socket.emit("watch", watchRef.current);
  }, [watched, live]);
};
